import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { PageNextButton } from "../Buttons/Pdf/PageNextButton";
import { PagePreviousButton } from "../Buttons/Pdf/PagePreviousButton";
import { SearchDocumentButton } from "../Buttons/Pdf/SearchDocumentButton";
import { SearchNextButton } from "../Buttons/Pdf/SearchNextButton";
import { SearchPreviousButton } from "../Buttons/Pdf/SearchPreviousButton";
import { SidebarButton } from "../Buttons/Pdf/SidebarButton";
import { ZoomInButton } from "../Buttons/Pdf/ZoomInButton";
import { ZoomOutButton } from "../Buttons/Pdf/ZoomOutButton";
import { AreaBody } from "../General/AreaBody";
import { FullScreen } from "../General/FullScreen";
import { Toolbar } from "../General/Toolbar";
import * as UiStore from "../States/AreaUiInfo";
import { AppStore } from "../Stores/AppStore";
import { DialogStore } from "../Stores/DialogStore";
import { Strings } from "../Stores/StringsStore";
import { ReaderToolbarPart, ReaderToolbarMode } from "./ReaderToolbarPart";

export interface IPDFProps extends UiStore.IAreaUiInfo {
	id: string;
	searchTerm: string;
	docId: string;
	docLanguage: string;
	collectionId: string;
	downloadName: string;
	canDownload: boolean;
	fileName: string;
	originalFilename: string;
	isDeleted: boolean;
}

@observer
export class PDF extends React.Component<IPDFProps, {}> {

	iframeStyles: React.CSSProperties;
	divStyleNone: React.CSSProperties;
	sideStyle: React.CSSProperties;
	@observable toggleSearchDoc: boolean = false;
	private searchInput: HTMLInputElement;

	refs: {
		[key: string]: Element;
		pdfIframe: HTMLIFrameElement;
	}

	componentWillUnmount() {
		let iframeContentWindow: any = this.refs.pdfIframe.contentWindow;
		if (iframeContentWindow && iframeContentWindow.PDFViewerApplication) {
			iframeContentWindow.PDFViewerApplication.close();
		}
		else {
			console.log("can't close PDFViewerApplication");
		}
	}

	componentWillMount(): void {
		this.iframeStyles = {
			width: "100%"
			//"float":"right"
		};
		this.divStyleNone = {
			display: "none"
		};
		this.sideStyle = {
			width: "200px"
		};
	}

	componentDidMount() {
		if (this.props.searchTerm) {
			this.searchInput.value = this.props.searchTerm;
			this.toggleSearchDoc = true;
		}
	}

	handleSearchDoc = () => {
		this.toggleSearchDoc = !this.toggleSearchDoc;
	}

	@computed get findBarCss() {
		return !this.toggleSearchDoc ? { display: "none" } : null;
	}

	@computed get source(): string {
		let id = this.props.id;
		let path = encodeURI(`/files/${id}.pdf`);
		return "/pdfjs/customviewer.html?file=" + path + "#locale=" + AppStore.context.sessionDetails.uiLanguage;
	}

	onLoad() {
		let asideButton = document.getElementById("cusSidebarToggle");
		if (asideButton && asideButton.classList.contains("is--button-highlight")) {
			asideButton.title = Strings.reader.readerAreaAsideButtonExpandedTooltip;
		}
		else {
			asideButton.title = Strings.reader.readerAreaAsideButtonCollapsedTooltip;
		}
	}

	render(): JSX.Element {
		return <AreaBody>
			<FullScreen>
				<Toolbar>
					<div id="toolbarContainer">
						<div id="toolbarViewer">
							<span className="buttongroup is--buttongroup-left">
								<SidebarButton id="cusSidebarToggle" />
								<span className="toolbar__splitter"></span>
								<SearchDocumentButton id="cusViewFind" onClick={this.handleSearchDoc} on={this.toggleSearchDoc} pressed={this.toggleSearchDoc} />
								<PagePreviousButton id="cusPrevious" />
								<PageNextButton id="cusNext" />
								<span className="toolbar__splitter"></span>

								<input type="number" id="pageNumber" className="toolbarField pageNumber is--s-display-hide" size={4} tabIndex={15} style={{ width: "35px" }} />
								<span id="numPages" className="toolbarLabel is--s-display-hide"></span>
								<span className="toolbar__splitter is--s-display-hide"></span>

								<ZoomOutButton id="cusZoomOut" />
								<ZoomInButton id="cusZoomIn" />
								<span id="cusScaleSelectContainer" className="dropdownToolbarButton">
									<select id="cusScaleSelect" title="Zoom" tabIndex={23} data-l10n-id="zoom" defaultValue="auto">
										<option id="pageAutoOption" title="" value="auto" data-l10n-id="page_scale_auto">Automatic Zoom</option>
										<option id="pageActualOption" title="" value="page-actual" data-l10n-id="page_scale_actual">Actual Size</option>
										<option id="pageFitOption" title="" value="page-fit" data-l10n-id="page_scale_fit">Fit Page</option>
										<option id="pageWidthOption" title="" value="page-width" data-l10n-id="page_scale_width">Full Width</option>
										<option id="cusCustomScaleOption" title="" value="custom" hidden={true}></option>
										<option title="" value="0.5" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 50 }'>50%</option>
										<option title="" value="0.75" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 75 }'>75%</option>
										<option title="" value="1" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 100 }'>100%</option>
										<option title="" value="1.25" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 125 }'>125%</option>
										<option title="" value="1.5" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 150 }'>150%</option>
										<option title="" value="2" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 200 }'>200%</option>
										<option title="" value="3" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 300 }'>300%</option>
										<option title="" value="4" data-l10n-id="page_scale_percent" data-l10n-args='{ "scale": 400 }'>400%</option>
									</select>
								</span>
							</span>
							<ReaderToolbarPart
								id={this.props.id}
								docId={this.props.docId}
								docLanguage={this.props.docLanguage}
								collectionId={this.props.collectionId}
								downloadName={this.props.downloadName}
								canDownload={this.props.canDownload}
								mode={ReaderToolbarMode.PDF}
								fileName={this.props.fileName}
								extension=".pdf"
								isDeleted={this.props.isDeleted}
								originalFilename={this.props.originalFilename}
							/>
						</div>
					</div>

					<div className="findbar hidden doorHanger hiddenSmallView" id="cusFindbar" style={this.findBarCss}>

						<span className="buttongroup is--buttongroup-left">
							<input ref={elem => this.searchInput = elem} id="cusFindInput" className="toolbarField" tabIndex={91} type="text" />
							<SearchPreviousButton id="cusFindPrevious" />
							<SearchNextButton id="cusFindNext" />
							<span className="toolbar__splitter"></span>
							<label htmlFor="cusFindHighlightAll" className="toolbarLabel" data-l10n-id="find_highlight">
								<input type="checkbox" id="cusFindHighlightAll" className="toolbarField" tabIndex={94} />
								<span>{Strings.reader.highlightAllText}</span>
							</label>
							<label htmlFor="cusFindMatchCase" className="toolbarLabel" data-l10n-id="find_match_case_label">
								<input type="checkbox" id="cusFindMatchCase" className="toolbarField" tabIndex={95} />
								<span>{Strings.reader.matchCaseText}</span>
							</label>
						</span>

						<div className="findbar__additional">
							<span id="cusFindResultsCount" className="toolbarLabel hidden"></span>
							<span id="cusFindMsg" className="toolbarLabel hidden"></span>
						</div>

					</div>

				</Toolbar>

				<div className="area-grid__body-content-iframe" id="mainContainer">
					<iframe ref="pdfIframe" style={this.iframeStyles} src={this.source} onLoad={this.onLoad} id="viewer" />
				</div>
			</FullScreen>
		</AreaBody>;
	}
}
